// L03 · The Concept of Tuesday (SPEC §3, Act I).
// Same grid UI as L02 — "select all squares containing the concept of Tuesday" — over
// nine abstract specimens. There is no correct answer; selecting nothing is itself an
// answer (the machine respects the refusal). Always passes.
//
// Traits (§3): `abstract` if you found the concept somewhere; `defiant` for the empty
// submit (you hold that Tuesday cannot be photographed).

import { el } from "@/ui/chrome.ts";
import { runLevel, promptEl, buildGrid, type Tile } from "@/levels/util.ts";
import { L03 as V, type VoiceLine } from "@/content/voice.ts";
import type { Level, LevelContext, LevelResult } from "@/levels/types.ts";

const TILES: Tile[] = [
  { emoji: "⏰", cap: "an alarm at 6:59" }, // 0
  { emoji: "🌧️", cap: "rain on the window" }, // 1
  { emoji: "🥪", cap: "a half-eaten sandwich" }, // 2
  { emoji: "🚶", cap: "commuters" }, // 3
  { emoji: "📅", cap: "a calendar with no dates" }, // 4
  { emoji: "📭", cap: "an empty inbox" }, // 5
  { emoji: "☕", cap: "the third coffee" }, // 6
  { emoji: "🕘", cap: "9 a.m." }, // 7
  { emoji: "☁️", cap: "an overcast sky" }, // 8
];

const SANDWICH = 2;
const CALENDAR = 4;
const INBOX = 5;
const MANY_THRESHOLD = 6;

function verdict(sel: Set<number>): VoiceLine {
  if (sel.size === 0) return V.empty!;
  if (sel.has(SANDWICH)) return V.sandwich!;
  if (sel.has(CALENDAR)) return V.calendar!;
  if (sel.has(INBOX)) return V.inbox!;
  if (sel.size >= MANY_THRESHOLD) return V.many!;
  return V.some!;
}

export const L03: Level = {
  id: "L03",
  act: 1,
  title: "The Concept of Tuesday",

  mount(host: HTMLElement, ctx: LevelContext): Promise<LevelResult> {
    return runLevel(ctx, (done) => {
      host.append(promptEl("Select all squares containing the concept of Tuesday."));
      const { root, selected } = buildGrid(TILES);
      host.append(root);

      const verify = el("button", "btn");
      verify.textContent = "Verify";
      verify.addEventListener("click", () => {
        const { line, traits } = verdict(selected);
        done({ pass: true, line, traits });
      });
      host.append(verify);
    });
  },
};
